import { z } from "zod";

import {
  recipeProductTitleEnum,
  nibblesProductTitleEnum,
  sprinklesProductTitleEnum,
  discountCodeTypeEnum,
} from "../types/enums";
import { defaultOngoingDeliveryFee } from "../constants/defaults";

// --- Shared structural defaults ---

export const defaultFalseSchema = z.boolean().default(false);
export const defaultTrueSchema = z.boolean().default(true);
export const defaultEmptyStringSchema = z.string().default("");
export const defaultZeroSchema = z.number().default(0);

// --- Address ---

export const addressFormDataSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  address1: z.string().optional(),
  address2: z.string().optional(),
  city: z.string().optional(),
  province: z.string().optional(),
  countryCode: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .optional(),
  company: z.string().optional(),
  phoneNumber: z.string().optional(),
  postcode: z.string().optional(),
});

export const shippingAddressSchema = addressFormDataSchema.omit({
  countryCode: true,
});

export const billingAddressSchema = addressFormDataSchema.omit({
  province: true,
});

export type TAddressFormData = z.infer<typeof addressFormDataSchema>;
export type TShippingAddress = z.infer<typeof shippingAddressSchema>;
export type TBillingAddress = z.infer<typeof billingAddressSchema>;

// --- Delivery ---

export const deliveryDateSchema = z.string().optional();
export const trialDeliveryFeeSchema = defaultZeroSchema;
export const ongoingDeliveryFeeSchema = z.number().default(
  defaultOngoingDeliveryFee,
);

export type TDeliveryDate = z.infer<typeof deliveryDateSchema>;
export type TTrialDeliveryFee = z.infer<typeof trialDeliveryFeeSchema>;
export type TOngoingDeliveryFee = z.infer<typeof ongoingDeliveryFeeSchema>;

// --- Recipes & Treats ---

export const recipeItemSchema = z.object({
  type: recipeProductTitleEnum,
  quantity: z.number(),
});

export const recipeSelectionSchema = z.object({
  recipes: z.array(recipeItemSchema),
  totalQuantity: z.number(),
  // True once selectedRecipes has been written under the shredded variant.
  // TODO: remove with the shredded A/B test.
  initialisedWithShredded: z.boolean().optional(),
});

export const treatItemSchema = z.object({
  type: z.literal("SPRINKLES"),
  added: z.boolean(),
});

export type TRecipeItem = z.infer<typeof recipeItemSchema>;
export type TRecipeSelection = z.infer<typeof recipeSelectionSchema>;
export type TTreatItem = z.infer<typeof treatItemSchema>;

// --- Upsell Items ---

export const nibblesUpsellItemSchema = z.object({
  type: nibblesProductTitleEnum,
  added: z.boolean().default(false),
  discountedPrice: z.number().default(0),
  originalPrice: z.number().default(0),
  quantity: z.number().default(0),
});

export const sprinklesUpsellItemSchema = z.object({
  type: sprinklesProductTitleEnum,
  added: z.boolean().default(false),
  discountedPrice: z.number().default(0),
  originalPrice: z.number().default(0),
  quantity: z.number().default(0),
});

export type TNibblesUpsellItem = z.infer<typeof nibblesUpsellItemSchema>;
export type TSprinklesUpsellItem = z.infer<typeof sprinklesUpsellItemSchema>;

// --- Plan & Discount ---

export const discountSchema = z.object({
  discountCode: defaultEmptyStringSchema,
  type: discountCodeTypeEnum.optional(),
  value: z.string().optional(),
});

export type TDiscount = z.infer<typeof discountSchema>;

// --- CAPI (Conversions API tracking data) ---

const capiDeliveryCategoryEnum = z.enum([
  "in_store",
  "curbside",
  "home_delivery",
]);

export const capiContentSchema = z.object({
  id: z.string().optional(),
  quantity: z.number().optional(),
  itemPrice: z.number().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  category: z.string().optional(),
  brand: z.string().optional(),
  deliveryCategory: capiDeliveryCategoryEnum.optional(),
});

export const capiCustomDataSchema = z.object({
  value: z.number().optional(),
  currency: z.string().optional(),
  contentName: z.string().optional(),
  contentCategory: z.string().optional(),
  contentIds: z.array(z.string()).optional(),
  contents: z.array(capiContentSchema).optional(),
  contentType: z.enum([ "product", "product_group" ]).optional(),
  orderId: z.string().optional(),
  predictedLtv: z.number().optional(),
  numItems: z.number().optional(),
  searchString: z.string().optional(),
  status: z.string().optional(),
  itemNumber: z.string().optional(),
  deliveryCategory: capiDeliveryCategoryEnum.optional(),
  customProperties: z.record(z.string(), z.unknown()).optional(),
});

export type TCapiContent = z.infer<typeof capiContentSchema>;
export type TCapiCustomData = z.infer<typeof capiCustomDataSchema>;
